import {
	Plugin,
	View,
	Rect, global, logWarning,
	// uid
} from 'ckeditor5';
import {
	debounce
}
from 'es-toolkit/compat';

export class PaginationLooker extends Plugin {

	_recalculatePageBreaksDebounced = debounce(() => {
		if (!this.calculatingPageBreaks) {
			this._recalculatePageBreaks()
		}
	}, 0xc8);

	_pagesContainer = null;

	static get pluginName() {
		return 'PaginationLooker';
	}

	static get isOfficialPlugin() {
		return false;
	}

	static get isPremiumPlugin() {
		return false;
	}

	calculatingPageBreaks = false;
	
	init() {
		this.set({
			'pageBreaks': []
		})

		if (!this.editor.config.get('pagination')) {
			return logWarning('pagination-config-not-found', this.editor.config);
		}

		const paginationEditing = this.editor.plugins.get('PaginationEditing');

		this.bind('isEnabled').to(paginationEditing);

		this.editor.ui.once('ready', () => this._onUiReady());
		
		this.on('change:isEnabled', (__2c1b79, __362efa, isEnabled) => {
			if (isEnabled) {
				this._recalculatePageBreaksDebounced();
			}
		});
	}
	
	destroy() {
		super.destroy();
		
		if (this._pagesContainer) {
			this._recalculatePageBreaksDebounced.cancel();
			this._pagesContainer.destroy();
			this._pagesContainer.element.remove();
		}
	}
	
	_onUiReady() {
		const editor = this.editor;
		const editingView = editor.editing.view;
		const attachedDomRoot = this._getAttachedDomRoot();

		if (!attachedDomRoot || 0 === attachedDomRoot.childElementCount) {
			return void editor.ui.once('update', () => this._onUiReady());	
		}

		const { pageWidth,  pageHeight } = editor.config.get('pagination')
		const { top, left, bottom, right } = this._configuredPaddings;

		this._pagesContainer = new View(editor.locale);

		const pagesContainer = this._pagesContainer;

		pagesContainer.set('class', '');

		pagesContainer.setTemplate({
			'tag': 'div',
			'attributes': {
				'style': {
					'columnWidth': pageWidth,
					'width': pageWidth,
					'height': 'calc( ' + pageHeight + ' - ' + top + ' - ' + bottom + ' + 0.5mm )',
					'minWidth': 'auto',
					'minHeight': 'auto',
					'padding': '0 ' + right + ' 0 ' + left,
					'columnGap': 'calc(' + left + ' + ' + right + ' + 100px)',
					'border': 0,
					'boxSizing': 'border-box',
					'columnCount': 'auto',
					'columnFill': 'auto',
					'overflow': 'hidden',
					'position': 'absolute',
					'top': '-99999px',
					'left': '-99999px'
				},
				'class': pagesContainer.bindTemplate.to('class')
			}
		});
		
		pagesContainer.render();
		attachedDomRoot.parentNode.insertBefore(pagesContainer.element, attachedDomRoot.nextSibling);
		
		this.listenTo(editingView, 'render', () => this._recalculatePageBreaksDebounced(), {
			'priority': 'low'
		});
		
		// this._recalculatePageBreaksDebounced();
	}
	
	_getAttachedDomRoot() {
		for (const documentRoot of this.editor.editing.view.domRoots.values()) {
			if (documentRoot.ownerDocument.body.contains(documentRoot)) {
				return documentRoot;
			}
		}

		return null;
	}
	
	async _recalculatePageBreaks() {
		if (!this.isEnabled) {
			return Promise.resolve();
		}

		const editingView = this.editor.editing.view;
		const pagesContainer = this._pagesContainer;
		const attachedDomRoot = this._getAttachedDomRoot();

		if (!attachedDomRoot) {
			return Promise.resolve();
		}

		this.calculatingPageBreaks = true;

		for (pagesContainer.class = ['ck-reset', 'ck-pagination-view', ...attachedDomRoot.classList].join(' '); pagesContainer.element.firstChild;) {
			pagesContainer.element.firstChild.remove();
		}

		const editingDomRoots = Array.from(editingView.domRoots);

		editingDomRoots.sort(
			([, editingDomRoot], [, currentIndex]) => editingDomRoot.compareDocumentPosition(currentIndex) === Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1
		);
		
		for (const [rootName, domRoot] of editingDomRoots) {
			const clonedDomRoot = domRoot.cloneNode(true);

			if('attributeStyleMap' in clonedDomRoot) {
				clonedDomRoot.attributeStyleMap.clear()
			}
			
			clonedDomRoot.dataset.rootName = rootName;
			clonedDomRoot.className = 'ck-content ck-pagination_enabled';
			clonedDomRoot.removeAttribute('id');
			clonedDomRoot.tabIndex = -1;
			pagesContainer.element.append(clonedDomRoot);
		}

		const imageElement = Array.from(pagesContainer.element.querySelectorAll('img'));

		return function(image) {
			return Promise.all(image.map(race => Promise.race([imageReady(race), timeout(0x3e8)])));
		}(imageElement).then(() => {
			for (const closestElement of imageElement.filter(element => !element.closest('table'))) {
				if (closestElement.offsetParent) {
					const computedStyle = global.window.getComputedStyle(closestElement);
					const spanElement = global.document.createElement('span');

					for (const style of computedStyle) {
						 spanElement.style.setProperty(style, computedStyle[style]);
					}
					
					spanElement.style.backgroundImage = 'url( ' + closestElement.src + ' )';
					spanElement.style.breakInside = 'avoid';

					if  ('PICTURE' === closestElement.parentElement?.tagName) {
						closestElement.parentElement.replaceWith(spanElement);
					} else {
						closestElement.replaceWith(spanElement);
					}
				}
			}

			return new Promise(resolve => global.window.requestAnimationFrame(() => resolve()));
		}).then(() => {
			this._updatePageBreaksData();
		});
	}
	
	_updatePageBreaksData() {
		const pagesContainer = this._pagesContainer.element;

		if (!pagesContainer.ownerDocument.body.contains(pagesContainer)) {
			this.calculatingPageBreaks = false;
			return;
		}
		
		const containerRect = new Rect(pagesContainer).excludeScrollbarsAndBorders();
		const containerWidth = Math.floor(containerRect.width + 0x64);
		const offsetLeft = Math.floor(containerRect.left - pagesContainer.scrollLeft);

		const pageStarterInfos = findPageStarterInfos(pagesContainer, offsetLeft, containerWidth, containerRect.height).map(
			info => this._mapPageStarterInfoToPageBreakInfo(info)
		);

        const validPageBreaks = pageStarterInfos.every(info => !!info);

		if (validPageBreaks) {
			this.pageBreaks = pageStarterInfos.filter(
				(current, _key, models) => {
					const exisitingStart = models.map(({ modelRange }) => modelRange.start.path[0]);

					return !exisitingStart.includes(current.modelRange.start.path[0] + 1)
				}
			)

			this.calculatingPageBreaks = false;
		}
	}
	
	_mapPageStarterInfoToPageBreakInfo(pageStarterInfos) {
		const editingView = this.editor.editing.view;
		const editingMapper = this.editor.editing.mapper;
		const domConverter = editingView.domConverter;
		const pageStarterInfo = pageStarterInfos.path.shift();
		const pagesContainerRootName = this._pagesContainer.element.childNodes[pageStarterInfo].dataset.rootName;
		const domRoot = editingView.getDomRoot(pagesContainerRootName);

		if (!domRoot) {
			return null;
		}

        const getStarterInfoInDomRoot = function(currentRoot, starterInfo) {
			let selectedRoot = currentRoot;
			for (const path of starterInfo.path) {
				if (!selectedRoot) {
					return null;
				}

				selectedRoot = selectedRoot.childNodes[path];
			}

			return selectedRoot;
		};

		const starterInfoDomRoot = getStarterInfoInDomRoot(domRoot, pageStarterInfos);

		if (!starterInfoDomRoot) {
			return null;
		}

		const domPositionView = domConverter.domPositionToView(starterInfoDomRoot, pageStarterInfos.textOffset || 0);

		if (domPositionView) {
			if ('manual' === pageStarterInfos.type) { 
				return this._mapManualPageStarterInfoToPageBreakInfo(domPositionView, starterInfoDomRoot);
			} else {
				if ('text' === pageStarterInfos.type && editingMapper.toModelPosition(domPositionView).offset) {
					return this._mapTextPageStarterInfoToPageBreakInfo(domPositionView, starterInfoDomRoot, pageStarterInfos.textOffset);
				} else {
					return this._mapElementPageStarterInfoToPageBreakInfo(domPositionView, starterInfoDomRoot, pageStarterInfos.offset);
				}
			}
		}


		return null;
	}
	
	_mapManualPageStarterInfoToPageBreakInfo(domView, domNode) {
		const editorModel = this.editor.model;
		const mapperModelElement = this.editor.editing.mapper.toModelElement(domView.parent);
		const modelRange = editorModel.createRangeOn(mapperModelElement);

		return new PageBreakInfo({
			'type': 'manual',
			'modelRange': modelRange,
			'domNode': domNode,
			'offset': 0
		});
	}
	
	_mapTextPageStarterInfoToPageBreakInfo(domView, domNode, starterOffset) {
		const editorModel = this.editor.model;
		const editingMapper = this.editor.editing.mapper;
		const viewDomConverter = this.editor.editing.view.domConverter;

		if (starterOffset >= domNode.length) {
			return null;
		}

		const modelPosition = editingMapper.toModelPosition(domView);
		const modelRange = editorModel.createRange(modelPosition);
		const mappedViewAncestor = editingMapper.findMappedViewAncestor(domView);
		const viewDom = viewDomConverter.mapViewToDom(mappedViewAncestor);

		return new PageBreakInfo({
			'type': 'text',
			'modelRange': modelRange,
			'domNode': domNode,
			'offset': calculatePageBreakOffset(domNode, starterOffset, viewDom)
		});
	}
	
	_mapElementPageStarterInfoToPageBreakInfo(domView, starterDomNode, starterOffset) {
		const editorModel = this.editor.model;
		const editorEditing = this.editor.editing;
		const editingMapper = editorEditing.mapper;
		const viewDomConverter = editorEditing.view.domConverter;
		const viewAncestor = editingMapper.findMappedViewAncestor(domView);
		const modelAncestor = editingMapper.toModelElement(viewAncestor);

		const getTopMostParentBlockTouchingPosition = function(selectedElement) {
			const parentElement = selectedElement.parent;
			const documentModel = parentElement.root.document.model;

			const parentAncestors = parentElement.getAncestors({
				'parentFirst': true,
				'includeSelf': true
			});

			let topMostBlock = null;

			for (const parentAncestor of parentAncestors) {
				if (parentAncestor.is('rootElement')) {
					return topMostBlock;
				}

				if (!documentModel.createPositionBefore(parentAncestor).isTouching(selectedElement)) {
					return topMostBlock;
				}

				topMostBlock  = parentAncestor;
			}
			
			return topMostBlock;
		}(editorModel.createPositionBefore(modelAncestor));

		const currentElement = getTopMostParentBlockTouchingPosition || modelAncestor;

		let mapperViewElement = editingMapper.toViewElement(currentElement);
		let modelRange = editorModel.createRangeOn(currentElement);

		const editorPositionBefore = editorModel.createPositionBefore(currentElement).nodeBefore;

		if (editorPositionBefore && editorPositionBefore.is('element', 'imageBlock')) {
			const imageBlock = editingMapper.toViewElement(editorPositionBefore);
			const domImageBlock = viewDomConverter.mapViewToDom(imageBlock);
			const floatSyle = global.window.getComputedStyle(domImageBlock).float;

			if (floatSyle !== 'left' && floatSyle !== 'right') {
				
			} else {
				modelRange = editorModel.createRangeOn(editorPositionBefore);
			}

		}
		
		for (; mapperViewElement.parent !== mapperViewElement.root && 0 === mapperViewElement.index;) {
			mapperViewElement = mapperViewElement.parent;
		}

		const elementRect = new Rect(isElementNode(starterDomNode) ? starterDomNode : createDomRangeOn(starterDomNode));
		const calculatedHeight = starterOffset ? elementRect.height - starterOffset : 0;
		const domNode = viewDomConverter.mapViewToDom(mapperViewElement);
		const domNOdeRect = new Rect(domNode);

		return new PageBreakInfo({
			'type': 'element',
			'modelRange': modelRange,
			'domNode': domNode,
			'offset': Math.round(calculatedHeight) || viewAncestor !== findParent(domView) ? calculatedHeight + elementRect.top - domNOdeRect.top : 0
		});
	}

	get _configuredPaddings() {
		const pageMargins = this.editor.config.get('pagination.pageMargins');

		return Object.assign({
			'top': '10mm',
			'bottom': '10mm',
			'left': '10mm',
			'right': '10mm'
		}, pageMargins);
	}
}

export class PageBreakInfo {

	type;

	modelRange;

	_domNode;

	_offset;

	constructor({ type, modelRange, domNode, offset }) {
		this.type = type
		this.modelRange = modelRange
		this._domNode = domNode
		this._offset = offset;
	}

	get offset() {
		
		if (!this._domNode.ownerDocument.body.contains(this._domNode)){
			return 0;
		}

		const domNodeRect = new Rect(isElementNode(this._domNode) ? this._domNode : createDomRangeOn(this._domNode));

		if (this.type === 'manual') {
			return domNodeRect.top + domNodeRect.height / 2
		}

		return domNodeRect.top + this._offset;
	}

}

function calculatePageBreakOffset(nodeElement, starterInfo, viewDom) {
	const rangeOn = createDomRangeOn(nodeElement);

	const starterNode = function(currentView) {
		const  range = global.document.createRange();
		range.selectNodeContents(currentView);

		return range;
	}(viewDom);

	let multipleRange;

	if (starterInfo === 0) {
		multipleRange = createDomRangeMultiNode(starterNode.startContainer, starterNode.startOffset, nodeElement.parentNode, Array.from(nodeElement.parentNode.childNodes).indexOf(nodeElement))
	} else  {
		multipleRange = createDomRangeMultiNode(starterNode.startContainer, starterNode.startOffset, nodeElement, starterInfo);
	}
	
	const rangeTop = createDomRangeMultiNode(nodeElement, starterInfo, starterNode.endContainer, starterNode.endOffset)
	const rangeBottom = new Rect(multipleRange).bottom;

	return (new Rect(rangeTop).top + rangeBottom) / 2 - new Rect(rangeOn).top;
}

function findParent(selectedElement) {
	let currentParent = selectedElement.parent;

	for (; currentParent.is('__text') || currentParent.is('attributeElement');) {
		currentParent = currentParent.parent;
	}

	return currentParent;
}

function imageReady(imageElement) {
	if (imageElement.hasAttribute('width') && imageElement.hasAttribute('height') ) {
		return Promise.resolve()
	}

	return new Promise(resolve => {
		function imageCallback() {
			imageElement.removeEventListener('load', imageCallback);
			imageElement.removeEventListener('error', imageCallback);
			
			resolve();
		}

		if (imageElement.complete) {
			resolve()
		} else {
			imageElement.addEventListener('load', imageCallback);
			imageElement.addEventListener('error', imageCallback);
		}
	});
}

function timeout(delay) {
	return new Promise(resolve => setTimeout(resolve, delay));
}

function createDomRangeOn(node) {
    const range = global.document.createRange();
    range.selectNode(node);

    return range;
}

function createDomRange(node, startOffset, endOffset) {
    const range = global.document.createRange();
    range.setStart(node, startOffset);
    range.setEnd(node, endOffset);

    return range;
}

function createDomRangeMultiNode(startNode, startOffset, endNode, endOffset) {
    const range = global.document.createRange();
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);

    return range;
}

function findPageStarterInfos(container, offsetLeft, pageWidth, pageHeight, isFirstPage) {
	const starterInfos = [];

	for (const [currentIndex, child] of container.childNodes.entries()) {
		let starters = [];

		if (isElementNode(child) && child.offsetParent && ['static', 'relative'].includes(global.window.getComputedStyle(child).position)) {
			starters = findPageStartersInElementNode(child, currentIndex, offsetLeft, pageWidth, pageHeight, isFirstPage) 
		} else if (isTextNode(child)) {
			starters = findPageStartersInTextNode(child, currentIndex, offsetLeft, pageWidth, isFirstPage)
		}
		
		if(starters.length) {
			starterInfos.push(...starters);
			offsetLeft += starters.length * pageWidth;
			isFirstPage = false;
		}
	}

	return starterInfos;
}


function findPageStartersInElementNode(elementNode, currentIndex, offsetIndex, pageWidth, pageHeight, isFirstPage) {
	const startersInElement = [];

	var previousSibling  = elementNode.previousSibling;

	if (isElementNode(previousSibling) && Boolean(previousSibling.classList.contains('page-break') || previousSibling.closest('.page-break'))) {
		startersInElement.push({
			'type': 'manual',
			'offset': 0,
			'path': [currentIndex - 1]
		});

		isFirstPage = true;
	}

	if ('table-row' === global.window.getComputedStyle(elementNode).display) {

		let currentHeight = 0;
		let trueOrFalse = false;

		for (const nodeClientRect of elementNode.getClientRects()) {
			currentHeight += nodeClientRect.height
			
			if(nodeClientRect.left >= offsetIndex) {
				trueOrFalse = true
			}
		}

		if (currentHeight < pageHeight && trueOrFalse) {
			startersInElement.push({
				'type': 'element',
				'offset': 0,
				'path': [currentIndex]
			})

			return startersInElement;
		}
	}

	const closestToFigureImage = elementNode.closest('figure.image');

	const ignoreFigureImage = function(closestElement) {
		let shouldIgnore = false;

		const closestParent = closestElement.parentElement;
		const closestRootName = closestParent.dataset.rootName;
		const nextElementSibling = closestElement.nextElementSibling;
		const previousElementSibling = closestElement.previousElementSibling;
		const floatSyle = nextElementSibling && global.window.getComputedStyle(nextElementSibling).float;
		const hasFakeSelectionClassName = nextElementSibling && 'ck-fake-selection-container' === nextElementSibling.className;

		if (!closestRootName && !nextElementSibling && !previousElementSibling) {
			shouldIgnore = true;
		}

		if (closestRootName && !previousElementSibling && hasFakeSelectionClassName) {
			shouldIgnore = true;
		}

		if (closestRootName && !previousElementSibling && floatSyle !== 'none') {
			shouldIgnore = true;
		}

		if (shouldIgnore) {
			return false;
		}

		return true;
	};

	if (closestToFigureImage && ignoreFigureImage(closestToFigureImage)) {
		const closestFloatStyle = global.window.getComputedStyle(closestToFigureImage).float;

		if ('left' === closestFloatStyle || 'right' === closestFloatStyle) {
			return startersInElement;
		}
	}

	const pageStarterInfos = findPageStarterInfos(elementNode, offsetIndex, pageWidth, pageHeight, isFirstPage);

	if (pageStarterInfos.length) {
		for (const starterInfo of pageStarterInfos) {
			 startersInElement.push({
				'type': starterInfo.type,
				'offset': starterInfo.offset,
				'textOffset': starterInfo.textOffset,
				'path': [currentIndex, ...starterInfo.path]
			});
			offsetIndex += pageWidth;
			isFirstPage = false;
		}

		return startersInElement;
	}

	const getLeafNodes = function(leafNode, lCurrentIndex, lOffsetIndex, lPageHeight, lIsFirstPage) {
		const starters = [];
		if ('table-cell' === global.window.getComputedStyle(leafNode).display) {
			return [];
		}

		const clientRects = [];

		for (const clientRect of leafNode.getClientRects()) {
			if (clientRect.left >= lOffsetIndex) {
				for (const selectedRect of clientRects) {
					selectedRect.height += clientRect.height;
				}

				clientRects.push({
					'height': clientRect.height
				})
				
				lOffsetIndex += lPageHeight;
			}
		}
			
		for (const clientRect of clientRects) {
			if (!lIsFirstPage) {
				starters.push({
					'type': 'element',
					'offset': clientRect.height,
					'textOffset': 0,
					'path': [lCurrentIndex]
				});
			}
			
			lIsFirstPage = false;
		}

		return starters;
	};

	startersInElement.push(...getLeafNodes(elementNode, currentIndex, offsetIndex, pageWidth, isFirstPage));

	return startersInElement;
}

function findPageStartersInTextNode(textNode, currentIndex, offsetLeft, pageHeight, isFirstPage) {
	const rangeOn = createDomRangeOn(textNode);
	const startersInText = [];

	let currentOffset = 0;

	for (const range of rangeOn.getClientRects()) {
		if (range.left >= offsetLeft) {
			const textOffset = findLastTextOffsetOnPage(textNode, currentOffset, offsetLeft, pageHeight);
			
			if(!isFirstPage) {
				startersInText.push({
					'type': 'text',
					'path': [currentIndex],
					'textOffset': textOffset
				});
			}
			
			currentOffset = textOffset;
			offsetLeft += pageHeight;
			isFirstPage = false;
		}
	}
	return startersInText;
}

function findLastTextOffsetOnPage(textNode, offsetStart, pageLeft, pageHeight) {
	
	let low = offsetStart;
	let high = textNode.length;

	for (; high - low > 1;) {
		const mid = Math.ceil((low + high) / 2);
		const domRange = createDomRange(textNode, low, mid);
		const containsPageBreak = domRangeContainsPageBreak(domRange, pageLeft, pageHeight);

		if (containsPageBreak) {
			high = mid;
		} else {
			low = mid;
		}
	}

	return low;
}

function domRangeContainsPageBreak(domRange, pageLeft, pageHeight) {
	for (const rangeRect of domRange.getClientRects()) {
		if (rangeRect.left >= pageLeft && rangeRect.left < pageLeft + pageHeight) return true;
	}

	return false;
}

function isElementNode(node) {
	return node?.nodeType === Node.ELEMENT_NODE;
}

function isTextNode(node) {
	return node?.nodeType === Node.TEXT_NODE;
}